interface Formatter {
  startingAt: number;
  endingAt?: number;
  abbreviation: string;
  interval: number;
}

// 99.99999 => 99.99
const roundToDecimalPlace = (num: number, decimalPlacesCount = 2) => {
  return Number(num.toFixed(decimalPlacesCount));
};
const truncateToDecimal = (num: number, decimalPlacesCount = 2) => {
  const calcDec = Math.pow(10, decimalPlacesCount);
  return Math.trunc(num * calcDec) / calcDec;
};

const formatNumberWithCommasAndShortenAt = (
  numberToFormat: number,
  formatters: Formatter[]
): string => {
  const formatter: Formatter | undefined = formatters.find(
    (findFormatter: Formatter): boolean =>
      numberToFormat <= (findFormatter.endingAt || Number.POSITIVE_INFINITY) &&
      numberToFormat >= findFormatter.startingAt
  );
  if (formatter) {
    const numberDecimal = numberToFormat / formatter.interval;
    const isWholeNumber = numberDecimal % 1 === 0;
    const numberDecimalRounded = isWholeNumber
      ? numberDecimal
      : truncateToDecimal(numberDecimal, 1);
    return `${numberDecimalRounded.toLocaleString()}${formatter.abbreviation}`;
  }
  return numberToFormat.toLocaleString();
};

// formatNumberWithCommasAndShortenStartingAtThousand
// 1000 => 1K
// 1200 => 1.2K
// 10000 => 10K
// 100000 => 100K
// 1000000 => 1M
// 1200000 => 1.2M
// 12900000 => 12.9M
const formatNumberWithCommasAndShortenStartingAtThousand = (
  numberToFormat: number
): string =>
  formatNumberWithCommasAndShortenAt(numberToFormat, [
    {
      startingAt: 1000,
      endingAt: 999999,
      abbreviation: 'K',
      interval: 1000,
    },
    {
      startingAt: 1000000,
      abbreviation: 'M',
      interval: 1000000,
    },
  ]);

// formatNumberWithCommasAndShortenStartingAtHundredThousand
// 1000 => 1,000
// 1200 => 1,200
// 10000 => 10,000
// 100000 => 100K
// 120000 => 120K
// 1000000 => 1M
// 1200000 => 1.2M
// 12900000 => 12.9M
const formatNumberWithCommasAndShortenStartingAtHundredThousand = (
  numberToFormat: number
): string =>
  formatNumberWithCommasAndShortenAt(numberToFormat, [
    {
      startingAt: 100000,
      endingAt: 999999,
      abbreviation: 'K',
      interval: 1000,
    },
    {
      startingAt: 1000000,
      abbreviation: 'M',
      interval: 1000000,
    },
  ]);

// formatNumberWithCommasAndShortenStartingAtMillion
// 1000 => 1,000
// 10000 => 10,000
// 100000 => 100,000
// 1000000 => 1M
// 1200000 => 1.2M
// 12900000 => 12.9M
const formatNumberWithCommasAndShortenStartingAtMillion = (
  numberToFormat: number
): string =>
  formatNumberWithCommasAndShortenAt(numberToFormat, [
    {
      startingAt: 1000000,
      abbreviation: 'M',
      interval: 1000000,
    },
  ]);

// formatNumberWithCommas
// 1000 => 1,000
// 10000 => 10,000
// 100000 => 100,000
// 1000000 => 1,000,000
// 1200000 => 1,200,000
// 12900000 => 12,900,000
// 365000000 => 365,000,000
// 1000000000 => 1,000,000,000
const formatNumberWithCommas = (numberToFormat: number): string =>
  numberToFormat.toLocaleString();

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export {
  formatNumberWithCommasAndShortenStartingAtThousand,
  formatNumberWithCommasAndShortenStartingAtHundredThousand,
  formatNumberWithCommasAndShortenStartingAtMillion,
  formatNumberWithCommas,
  roundToDecimalPlace,
  formatBytes,
};
